import { apiClient } from '@/api/client';
import { PROFILE_QUERY_KEYS } from '@/api/queries/users';
import { authClient } from '@/config/auth-client';
import { authFetch, safeJson } from '@/features/auth/auth-fetch';
import { signInWithApple, signInWithGoogle, type SignInSocialOpts } from '@/features/auth/sign-in-social';
import { getQueryPersister } from '@/providers/query-provider';
import { createLogger } from '@/utils/logger';
import { useQueryClient } from '@tanstack/react-query';
import Constants from 'expo-constants';
import { Platform } from 'react-native';
import React, { createContext, useContext, useEffect, useRef } from 'react';

const logger = createLogger('USE_AUTH');

// --- Types ---

export interface AuthUser {
  id: string;
  email: string;
  emailVerified: boolean;
  name: string;
  image?: string | null;
  username?: string;
  displayUsername?: string;
  role?: string;
  banned?: boolean;
  accountType?: 'real' | 'demo' | 'sentinel';
  isDemo?: boolean;
  /**
   * True for a social user created with a placeholder handle who has not yet
   * claimed a real username. Mapped from the session's `username_pending` claim.
   */
  usernamePending?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

interface AuthResult<T = unknown> {
  data?: T;
  error?: { message?: string; status?: number; code?: string; [key: string]: unknown };
}

type AuthResultError = NonNullable<AuthResult['error']>;

/** One sign-in method linked to the current account. */
export interface LinkedAccount {
  /**
   * Better Auth provider id. `credential` means a password is set; anything
   * else is a social provider (`google`, `apple`, ...).
   */
  providerId: string;
}

type AuthContextType = {
  isLoggedIn: boolean;
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<AuthResult>;
  register: (email: string, password: string, name: string, username: string, inviteCode?: string) => Promise<AuthResult>;
  signInWithGoogle: (opts?: SignInSocialOpts) => Promise<void>;
  signInWithApple: (opts?: SignInSocialOpts) => Promise<void>;
  logout: () => Promise<void>;
  deleteAccount: (password?: string) => Promise<void>;
  /** True when the current user has a password (credential) account. */
  checkHasPassword: () => Promise<boolean>;
  /**
   * Every sign-in method linked to the current account. Rejects (rather than
   * returning an empty list) when the lookup fails, so a caller can never
   * mistake a failed request for "this account has no providers".
   */
  fetchLinkedAccounts: () => Promise<LinkedAccount[]>;
  /** Change an existing password; other devices are signed out. */
  changePassword: (currentPassword: string, newPassword: string) => Promise<AuthResult>;
  /** Give a social-only account its first password. */
  setPassword: (newPassword: string) => Promise<AuthResult>;
  /** Remove a linked sign-in method. */
  unlinkAccount: (providerId: string) => Promise<AuthResult>;
  /** Force a session re-fetch (bypassing the cookie cache). */
  refreshSession: () => Promise<void>;
  sendVerificationOTP: (email: string) => Promise<AuthResult>;
  verifyEmailOTP: (email: string, otp: string) => Promise<AuthResult>;
  checkUsernameAvailability: (username: string) => Promise<AuthResult<{ available: boolean }>>;
  forgetPassword: (email: string) => Promise<AuthResult>;
  resetPassword: (token: string, newPassword: string) => Promise<AuthResult>;
  isLoading: boolean;
  error: Error | null;
};

// --- Password / linked-account constants ---

/** Better Auth's provider id for an email + password account. */
const CREDENTIAL_PROVIDER_ID = 'credential';

/**
 * Custom auth-service route. Better Auth declares `setPassword` as
 * `serverOnly`, so there is no client method for it.
 */
const SET_PASSWORD_PATH = '/api/auth/password/set';

/**
 * `setPassword` hit an account that already has a password. Distinct from a
 * generic error so the UI can switch the user to change-password mode.
 */
export const PASSWORD_ALREADY_SET = 'PASSWORD_ALREADY_SET';

/**
 * Better Auth's rejection when unlinking would leave the user with no way to
 * sign in. Surfaced verbatim as a code so the UI can explain it.
 */
export const FAILED_TO_UNLINK_LAST_ACCOUNT = 'FAILED_TO_UNLINK_LAST_ACCOUNT';

const UNLINK_LAST_ACCOUNT_MESSAGE =
  'This is your only way to sign in. Set a password or link another provider first.';
const UNLINK_FAILED_MESSAGE = 'Could not unlink that account. Please try again.';
const LIST_ACCOUNTS_FAILED_MESSAGE = 'Could not load your sign-in methods.';
const SET_PASSWORD_FAILED_MESSAGE = 'Could not set your password. Please try again.';

const SET_PASSWORD_ERRORS: Readonly<Record<number, { code: string; message: string }>> = {
  400: { code: 'PASSWORD_INVALID', message: 'That password is not valid. Use at least 8 characters.' },
  401: { code: 'UNAUTHENTICATED', message: 'Your session expired. Please sign in again.' },
  409: {
    code: PASSWORD_ALREADY_SET,
    message: 'You already have a password. Use Change password instead.',
  },
};

const SET_PASSWORD_UNKNOWN_ERROR = { code: 'UNKNOWN', message: SET_PASSWORD_FAILED_MESSAGE };

// --- Helpers ---

/**
 * Turn a non-200 set-password response into an `AuthResult` error. The status
 * code is always carried through: screens must branch on it, never on message
 * text (a lesson the reset-password screen learned the hard way).
 */
const buildSetPasswordError = (
  status: number,
  body: Record<string, unknown>
): AuthResultError => {
  const mapped = SET_PASSWORD_ERRORS[status] ?? SET_PASSWORD_UNKNOWN_ERROR;
  const serverMessage = typeof body.error === 'string' ? body.error : undefined;
  return { status, code: mapped.code, message: serverMessage ?? mapped.message };
};

/** Better Auth surfaces its error code inline or nested under `body`. */
const getErrorCode = (error: unknown): string | undefined => {
  const candidate = error as { code?: unknown; body?: { code?: unknown } } | null;
  const code = candidate?.code ?? candidate?.body?.code;
  return typeof code === 'string' ? code : undefined;
};

const isLastAccountError = (error: unknown): boolean =>
  getErrorCode(error) === FAILED_TO_UNLINK_LAST_ACCOUNT;

const lastAccountError = (): AuthResultError => ({
  code: FAILED_TO_UNLINK_LAST_ACCOUNT,
  message: UNLINK_LAST_ACCOUNT_MESSAGE,
});

const getPasswordResetRedirectUrl = () => {
  if (Platform.OS === 'web') {
    return `${typeof window !== 'undefined' ? window.location.origin : ''}/(auth)/reset-password`;
  }
  const schemeValue = Constants.expoConfig?.scheme;
  const scheme = Array.isArray(schemeValue) ? schemeValue[0] : (schemeValue || 'flavrly-dev');
  return `${scheme}://reset-password`;
};

// --- Context ---

const AuthContext = createContext<AuthContextType | null>(null);

// A social user who signs in on the login screen (no invite entered) but has no
// Flavrly account yet is rejected by the invite gate. Surface that as a single
// clear, invite-only message rather than the raw server text.
const INVITE_REQUIRED_MESSAGE = 'You need an invite to join Flavrly.';

const mapSocialError = (error: unknown, opts?: SignInSocialOpts): Error => {
  const message = error instanceof Error ? error.message : String(error);
  // Only the login path (no invite supplied) reinterprets an invite rejection as
  // "you need an invite". On the register path the specific server/pre-flight
  // message is more useful, so it passes through untouched.
  if (!opts?.inviteCode && /invite/i.test(message)) {
    return new Error(INVITE_REQUIRED_MESSAGE);
  }
  return error instanceof Error ? error : new Error('Could not sign in. Please try again.');
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { data: session, isPending, isRefetching, error, refetch } = authClient.useSession();
  const queryClient = useQueryClient();

  // Preserve previous valid session to prevent brief loggedOut flash during re-validation
  // (e.g. when returning to a browser tab after a few minutes)
  const prevSessionRef = useRef(session);
  if (session) {
    prevSessionRef.current = session;
  } else if (!isPending && !isRefetching) {
    // Definitively logged out (not mid-refetch) -- clear the cache
    prevSessionRef.current = null;
  }

  // Use current session if available; during re-validation, fall back to previous
  const isFetchingSession = isPending || isRefetching;
  const effectiveSession = session ?? (isFetchingSession ? prevSessionRef.current : null);

  const isLoggedIn = !!effectiveSession?.user;
  const rawSessionUser = effectiveSession?.user as any;
  const user: AuthUser | null = rawSessionUser
    ? {
        ...rawSessionUser,
        accountType: rawSessionUser.account_type,
        // Source isDemo from account_type (viewer is only ever real or demo);
        // fall back to the legacy is_demo flag during transition.
        isDemo: rawSessionUser.account_type
          ? rawSessionUser.account_type === 'demo'
          : rawSessionUser.is_demo === true || rawSessionUser.is_demo === 1,
        // snake_case claim -> camelCase; SQLite may send it as 1/0.
        usernamePending:
          rawSessionUser.username_pending === true ||
          rawSessionUser.username_pending === 1,
      }
    : null;
  const isLoading = isPending && !prevSessionRef.current; // Only true on initial boot (no prior session)

  // Invalidate profile cache when the user changes (login/logout/switch)
  const prevUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    const currentUserId = user?.id ?? null;
    if (prevUserIdRef.current !== currentUserId) {
      queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEYS.all });
      prevUserIdRef.current = currentUserId;
    }
  }, [user?.id, queryClient]);

  const login = async (email: string, password: string): Promise<AuthResult> => {
    try {
      const result = await authClient.signIn.email({ email, password });
      if (result.error) {
        return { error: result.error };
      }
      return { data: result.data };
    } catch (error: any) {
      logger.error('Login error:', error);
      return { error: { message: error.message || 'Login failed' } };
    }
  };

  const register = async (
    email: string,
    password: string,
    name: string,
    username: string,
    inviteCode?: string
  ): Promise<AuthResult> => {
    try {
      const signUpData: { email: string; password: string; name: string; username: string; inviteCode?: string } = {
        email,
        password,
        name,
        username,
      };
      if (inviteCode) {
        signUpData.inviteCode = inviteCode;
      }

      const result = await authClient.signUp.email(signUpData);
      if (result.error) {
        return { error: result.error };
      }
      return { data: result.data };
    } catch (error: any) {
      logger.error('Registration error:', error);
      return { error: { message: error.message || 'Registration failed' } };
    }
  };

  const logout = async () => {
    try {
      queryClient.clear();
      // Explicitly purge the persisted (on-disk) cache. queryClient.clear()
      // only empties memory; the persister's throttled subscription can lose
      // the race to write an empty blob before the session tears down, which
      // would leave one account's persisted data readable by the next.
      await getQueryPersister().removeClient();
      apiClient.clearTokenCache();
      await authClient.signOut();
    } catch (error) {
      logger.error('Logout error:', error);
      throw error;
    }
  };

  /**
   * The sign-in methods linked to the current account, via Better Auth's
   * listAccounts. The single listAccounts code path in the app.
   *
   * Older Better Auth payloads name the field `provider` rather than
   * `providerId`; both are normalised onto `providerId` here so callers only
   * ever deal with one shape.
   *
   * Rejects on failure. Returning an empty list instead would be dangerous:
   * "the lookup failed" and "this account has no password" must not look alike
   * to callers such as the delete-account flow.
   */
  const fetchLinkedAccounts = async (): Promise<LinkedAccount[]> => {
    const result = await (authClient as any).listAccounts();
    if (result?.error) {
      throw new Error(result.error.message || LIST_ACCOUNTS_FAILED_MESSAGE);
    }
    const accounts: Array<{ providerId?: string; provider?: string }> = result?.data ?? [];
    return accounts.map((account) => ({
      providerId: account.providerId ?? account.provider ?? '',
    }));
  };

  /**
   * Whether the current user has a password (credential) account.
   *
   * A social-only user (Google/Apple) has no password to re-auth with, so the
   * delete screen must not ask for one. Detected via the linked-account list:
   * a `credential` provider means a password is set.
   *
   * On error we fail SAFE (assume a password exists) so we never fall back to a
   * password-less deletion path for an account that actually has one.
   */
  const checkHasPassword = async (): Promise<boolean> => {
    try {
      const accounts = await fetchLinkedAccounts();
      return accounts.some((account) => account.providerId === CREDENTIAL_PROVIDER_ID);
    } catch (error) {
      logger.error('listAccounts error:', error);
      return true;
    }
  };

  /**
   * Permanently delete the current user's account.
   *
   * Order matters:
   *   1. Ask the gateway to delete/anonymize the user's data. If this fails we
   *      abort -- we must NOT delete the auth user while gateway data remains.
   *   2. Delete the Better Auth user. Password users re-auth with their
   *      password; social-only users delete via the current session ({}).
   *   3. Run the same teardown logout() performs so no stale session/cache
   *      lingers. Session loss triggers the root-layout redirect to the auth
   *      stack -- no manual navigation needed.
   */
  const deleteAccount = async (password?: string): Promise<void> => {
    try {
      // Step 1: gateway data deletion / anonymization (idempotent server-side)
      await apiClient.delete('/api/user/account');

      // Step 2: Better Auth user deletion. Only pass a password when the account
      // actually has one -- social-only users delete via their session.
      const hasPassword = await checkHasPassword();
      const deleteArgs = hasPassword ? { password } : {};
      const result = await (authClient as any).deleteUser(deleteArgs);
      if (result?.error) {
        throw new Error(result.error.message || 'Failed to delete account');
      }

      // Step 3: teardown -- mirror logout()
      await logout();
    } catch (error) {
      logger.error('Delete account error:', error);
      throw error;
    }
  };

  /**
   * Force a fresh session fetch, bypassing the cookie cache. Used after a
   * username claim so the `username_pending` claim flips and the gate unmounts.
   */
  const refreshSession = async (): Promise<void> => {
    try {
      await refetch({ query: { disableCookieCache: true } });
    } catch (error) {
      logger.error('Session refresh error:', error);
      throw error;
    }
  };

  const signInWithGoogleCtx = async (opts?: SignInSocialOpts): Promise<void> => {
    try {
      await signInWithGoogle(opts);
    } catch (error) {
      logger.error('Google sign-in error:', error);
      throw mapSocialError(error, opts);
    }
  };

  const signInWithAppleCtx = async (opts?: SignInSocialOpts): Promise<void> => {
    try {
      await signInWithApple(opts);
    } catch (error) {
      logger.error('Apple sign-in error:', error);
      throw mapSocialError(error, opts);
    }
  };

  const sendVerificationOTP = async (email: string): Promise<AuthResult> => {
    try {
      const result = await authClient.emailOtp.sendVerificationOtp({
        email,
        type: 'email-verification',
      });
      if (result.error) {
        return { error: result.error };
      }
      return { data: result.data };
    } catch (error: any) {
      logger.error('Send OTP error:', error);
      return { error: { message: error.message || 'Failed to send verification OTP' } };
    }
  };

  const verifyEmailOTP = async (email: string, otp: string): Promise<AuthResult> => {
    try {
      const result = await authClient.emailOtp.verifyEmail({ email, otp });
      if (result.error) {
        return { error: result.error };
      }
      return { data: result.data };
    } catch (error: any) {
      logger.error('Email OTP verification error:', error);
      return { error: { message: error.message || 'Email verification failed' } };
    }
  };

  const checkUsernameAvailability = async (username: string): Promise<AuthResult<{ available: boolean }>> => {
    try {
      const result = await (authClient as any).isUsernameAvailable({
        username: username.trim(),
      });
      if (result.error) {
        return { error: result.error };
      }
      return { data: { available: result.data?.available || false } };
    } catch (error: any) {
      logger.error('Username availability check error:', error);
      return { error: { message: error.message || 'Username check failed' } };
    }
  };

  const forgetPassword = async (email: string): Promise<AuthResult> => {
    try {
      const redirectTo = getPasswordResetRedirectUrl();
      const result = await (authClient as any).requestPasswordReset({
        email: email.trim(),
        redirectTo,
      });
      if (result.error) {
        return { error: result.error };
      }
      return { data: result.data };
    } catch (error: any) {
      logger.error('Forget password error:', error);
      return { error: { message: error.message || 'Password reset request failed' } };
    }
  };

  const resetPassword = async (token: string, newPassword: string): Promise<AuthResult> => {
    try {
      const result = await (authClient as any).resetPassword({ token, newPassword });
      if (result.error) {
        return { error: result.error };
      }
      return { data: result.data };
    } catch (error: any) {
      logger.error('Reset password error:', error);
      return { error: { message: error.message || 'Password reset failed' } };
    }
  };

  /**
   * Change the password of an account that already has one.
   *
   * `revokeOtherSessions` signs every other device out while keeping the
   * current one signed in -- a password change is usually a response to a
   * suspected compromise.
   */
  const changePassword = async (
    currentPassword: string,
    newPassword: string
  ): Promise<AuthResult> => {
    try {
      const result = await (authClient as any).changePassword({
        currentPassword,
        newPassword,
        revokeOtherSessions: true,
      });
      if (result.error) {
        return { error: result.error };
      }
      return { data: result.data };
    } catch (error: any) {
      logger.error('Change password error:', error);
      return { error: { message: error.message || 'Password change failed' } };
    }
  };

  /**
   * Give a social-only account its first password.
   *
   * Better Auth's `setPassword` is server-only (no HTTP route, no client
   * method), so this goes through our own auth-service route via `authFetch`
   * -- which is what carries the session on native.
   *
   * A 409 (a password already exists) is mapped to the distinct
   * `PASSWORD_ALREADY_SET` code so the UI can switch to change-password mode
   * instead of showing an error.
   */
  const setPassword = async (newPassword: string): Promise<AuthResult> => {
    try {
      const response = await authFetch(SET_PASSWORD_PATH, {
        method: 'POST',
        body: { newPassword },
      });
      const body = await safeJson(response);
      if (response.status !== 200) {
        return { error: buildSetPasswordError(response.status, body) };
      }
      return { data: body };
    } catch (error: any) {
      logger.error('Set password error:', error);
      return {
        error: {
          code: SET_PASSWORD_UNKNOWN_ERROR.code,
          message: error.message || SET_PASSWORD_FAILED_MESSAGE,
        },
      };
    }
  };

  /**
   * Remove a linked sign-in method.
   *
   * The server refuses to unlink the last one (`FAILED_TO_UNLINK_LAST_ACCOUNT`)
   * so a user cannot lock themselves out. That case is normalised into a
   * dedicated code plus readable copy, whether Better Auth returns it or
   * throws it, so the UI can explain the refusal rather than echo a raw string.
   */
  const unlinkAccount = async (providerId: string): Promise<AuthResult> => {
    try {
      const result = await (authClient as any).unlinkAccount({ providerId });
      if (result.error) {
        return {
          error: isLastAccountError(result.error)
            ? { ...result.error, ...lastAccountError() }
            : result.error,
        };
      }
      return { data: result.data };
    } catch (error: any) {
      logger.error('Unlink account error:', error);
      if (isLastAccountError(error)) {
        return { error: lastAccountError() };
      }
      return { error: { message: error.message || UNLINK_FAILED_MESSAGE } };
    }
  };

  return (
    <AuthContext.Provider value={{
      isLoggedIn,
      user,
      login,
      register,
      signInWithGoogle: signInWithGoogleCtx,
      signInWithApple: signInWithAppleCtx,
      logout,
      deleteAccount,
      checkHasPassword,
      fetchLinkedAccounts,
      changePassword,
      setPassword,
      unlinkAccount,
      refreshSession,
      sendVerificationOTP,
      verifyEmailOTP,
      checkUsernameAvailability,
      forgetPassword,
      resetPassword,
      isLoading,
      error,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
